import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as y from "yup";

import { MultiLevelMultipleCheckboxField } from "@/components/Form/Parts/index";
import styleButton from "@/styles/Button.module.scss";
import formStyles from "@/styles/Form.module.scss";
import processStyles from "@/styles/Process.module.scss";
import styles from "@/styles/Unsubscribe.module.scss";

import {
  useFormQuestionAnswerMutation,
  useFormQuestionsQuery,
} from "../../hooks/generated/graphql";
import TSButton from "../Buttons/TSButton";
import TSTextAreaField from "./Parts/TSTextAreaField";

import type {
  CheckboxQuestionProps,
  Option,
  Question,
  QuestionnaireFormType,
  TextQuestionProps,
} from "../../types/QuestionaireForm";
import type { FieldValues } from "react-hook-form";

// 質問レベルに応じて subTitle の「問」の数値が変化
const getSubTitle = (level: number, index: number) => `問${level}-${index}`;

function TextQuestion({
  register,
  question,
  subTitle,
  visible,
}: TextQuestionProps) {
  const key = question.key_name ?? "";
  return (
    <TSTextAreaField
      {...register(`answer.${key}`)}
      title={question.label || ""}
      help={question.help_text || ""}
      subTitle={subTitle}
      placeholder="コメント"
      required={question.required || false}
      visible={visible}
    />
  );
}

function CheckboxQuestion<FormController extends FieldValues>({
  control,
  question,
  errors,
  selectedQuestionValues,
  subTitle,
  visible,
}: CheckboxQuestionProps<FormController>) {
  if (!question.key_name) return null;
  const options =
    question.options?.filter((option): option is Option => option !== null) ||
    [];

  return (
    <MultiLevelMultipleCheckboxField
      control={control}
      name={`answer.${question.key_name}`}
      help={question.help_text || ""}
      required={question.required || false}
      options={options}
      title={question.label || ""}
      error={errors?.answer?.[question.key_name]?.message}
      subTitle={subTitle}
      selectedQuestionValues={selectedQuestionValues as () => void}
      defaultValue={[]}
      row={undefined}
      visible={visible}
    />
  );
}

export default function QuestionnaireForm({
  hideModal,
  showNotification,
  questionId,
  isSkipAble,
}: QuestionnaireFormType) {
  const [formQuestionAnswer] = useFormQuestionAnswerMutation({
    onCompleted: (data: any) => {
      if (data?.formQuestionAnswer?.answer_id) {
        hideModal();
        showNotification("アンケートに回答しました。", { time: 2000 });
      }
    },
  });

  const { data } = useFormQuestionsQuery({
    variables: {
      id: questionId,
    },
  });
  const questionFormData = useMemo(() => {
    return data?.formQuestions?.data || [];
  }, [data]);
  const questionFormDataTyped = questionFormData as Question[];

  const level1QuestionsOriginal = questionFormDataTyped?.filter(
    (q): q is Question => !!q?.schema_group_name?.includes("level1")
  );
  const level2QuestionsOriginal = questionFormDataTyped?.filter(
    (q): q is Question => !!q?.schema_group_name?.includes("level2")
  );

  const level3QuestionsOriginal = questionFormDataTyped?.filter(
    (q): q is Question => !!q?.schema_group_name?.includes("level3")
  );

  // hierarchy項目を追加
  const level1Questions = level1QuestionsOriginal?.map((element) => {
    if (!element?.key_name) return null;
    const hierarchyList: string[] = [];
    return { ...element, ...{ hierarchy: hierarchyList } };
  });

  // hierarchy項目を追加
  const level2Questions = level2QuestionsOriginal?.map((element) => {
    const hierarchyKey = element?.schema_group_name?.split("_")?.[1];
    if (!hierarchyKey) return null;
    return { ...element, ...{ hierarchy: [hierarchyKey] } };
    //    element.hierarchy = [hierarchyKey];
  });

  const level3Questions = level3QuestionsOriginal?.map((element) => {
    const level3hierarchyKey = element?.schema_group_name?.split("_")?.[1];
    if (!level3hierarchyKey) return null;

    const level2QuestionFromLevel3HierarchyKey = level2Questions.find(
      (element) => {
        if (!element?.options) return;
        return element?.options.some((option) => {
          if (!option) return;
          if (!option?.value) return;
          return option?.value?.includes(level3hierarchyKey);
        });
      }
    );

    const level2hierarchyKey =
      level2QuestionFromLevel3HierarchyKey?.schema_group_name?.split("_")?.[1];

    if (!level2hierarchyKey || !level3hierarchyKey) return null;

    return {
      ...element,
      ...{ hierarchy: [level2hierarchyKey, level3hierarchyKey] },
    };
  });

  const allQuestionWithHierarchy = level1Questions
    ?.concat(level2Questions)
    ?.concat(level3Questions);

  const level3QuestionsNotNull = level3Questions.filter((element) => !!element);

  // level2の質問が表示フラグをbooleanでもつステート
  const [level2QuestionsVisible, setLevel2QuestionsVisible] = useState<
    Question[]
  >([]);
  // level3の質問が表示フラグをbooleanでもつステート
  const [level3QuestionsVisible, setLevel3QuestionsVisible] = useState<
    Question[]
  >([]);
  // level1で選択済みのチェックボックスを管理するステート
  const [selectedLevel1Values, setSelectedLevel1Values] = useState<
    Record<string, string[]>
  >({});
  // level2で選択済みのチェックボックスを管理するステート
  const [selectedLevel2Values, setSelectedLevel2Values] = useState<
    Record<string, string[]>
  >({});

  // レベル1の選択肢変更ハンドラ
  const handleLevel1OptionChange = useCallback(
    (questionKey: string, selectedValues: string[]) => {
      // チェックボックスのチェックが外されて場合
      // どのチェックボックスのチェックが
      if (
        selectedLevel1Values?.[`${questionKey}`]?.length > selectedValues.length
      ) {
        // 外した項目
        const diffList = selectedLevel1Values?.[`${questionKey}`]?.filter(
          (element) => !selectedValues?.includes(element)
        );
        const removedKey = diffList?.[0];
      }

      // レベル1の選択肢の更新
      setSelectedLevel1Values((prev) => ({
        ...prev,
        [questionKey]: selectedValues,
      }));
    },
    [selectedLevel1Values]
  );

  // レベル2の選択肢変更ハンドラ
  const handleLevel2OptionChange = useCallback(
    (questionKey: string, selectedValues: string[]) => {
      setSelectedLevel2Values((prev) => ({
        ...prev,
        [questionKey]: selectedValues,
      }));
    },
    []
  );

  // レベル2の質問を更新する関数
  const updateLevel2QuestionsFromLevel1 = useCallback(() => {
    const selectedLevel1ValuesList = Object.values(selectedLevel1Values).flat();
    const newLevel2Questions = questionFormData.filter(
      (q: any): q is Question =>
        q !== null &&
        selectedLevel1ValuesList.some(
          (value) => q.schema_group_name?.startsWith(`level2_${value}`)
        )
    );
    setLevel2QuestionsVisible(newLevel2Questions);
  }, [selectedLevel1Values, questionFormData]);

  // レベル3の質問をlevel2のチェックボックスから更新する関数
  const updateLevel3QuestionsFromLevel2 = useCallback(() => {
    const selectedLevel2ValuesList = Object.values(selectedLevel2Values).flat();
    const newLevel3Questions = questionFormData.filter(
      (q: any): q is Question =>
        q !== null &&
        selectedLevel2ValuesList.some(
          (value) => q.schema_group_name?.startsWith(`level3_${value}`)
        )
    );
    setLevel3QuestionsVisible(newLevel3Questions);
  }, [selectedLevel2Values, questionFormData]);

  useEffect(() => {
    updateLevel2QuestionsFromLevel1();
  }, [updateLevel2QuestionsFromLevel1]);

  useEffect(() => {
    updateLevel3QuestionsFromLevel2();
  }, [updateLevel3QuestionsFromLevel2]);

  // バリデーション
  const schema = useMemo(() => {
    const answerSchema = (() => {
      if (questionFormData.length > 0) {
        return Object.fromEntries(
          questionFormData
            .filter(
              (data: any): data is Question =>
                data !== null && data.required === true
            )
            .map((data: any) => {
              if (data?.type === "string") {
                return [
                  data?.key_name || "",
                  y.string().required("回答を入力してください。"),
                ];
              } else if (data?.type === "const") {
                return [
                  data?.key_name || "",
                  y.array().min(1, "選択してください。"),
                ];
              }
              return [data?.key_name || "", y.mixed()];
            })
        );
      }
      return {};
    })();
    return y.object({
      answer: y.object(answerSchema),
      questionId: y.number(),
    });
  }, [questionFormData]);

  const { formState, handleSubmit, register, control } = useForm({
    resolver: yupResolver(schema),
    mode: "all",
  });
  const { errors, isValid, isSubmitting } = formState;

  const level2QuestionsVisibleSchemaGroupNameList = level2QuestionsVisible?.map(
    (element) => element?.schema_group_name
  );

  const level3QuestionsVisibleSchemaGroupNameList = level3QuestionsVisible?.map(
    (element) => element?.schema_group_name
  );

  // アンケート回答
  const handleAnswerSubmit = useCallback(
    async (data: any) => {
      try {
        const formattedAnswer = Object.entries(data.answer).reduce<
          Record<string, string | string[]>
        >((acc, [key, value]) => {
          // ループでの質問
          const currentQuestion = allQuestionWithHierarchy?.find(
            (element) => element?.key_name === key
          );
          // 選択をはずした項目は小要素含めデータを連携しない
          let notVisible = true;
          const schemaGroupName = currentQuestion?.schema_group_name || "";

          //  visibilityFromIntersection 定義する

          const visibleFromLevel1Selected = Object.values(selectedLevel1Values)
            .flat()
            .flat();

          const hierarchyList = currentQuestion?.hierarchy || [];

          const visibilityFromIntersection =
            visibleFromLevel1Selected?.filter((element) =>
              hierarchyList.includes(element)
            )?.length > 0;

          if (schemaGroupName?.includes("level1")) {
            notVisible = false;
          } else if (
            schemaGroupName?.includes("level2") &&
            !!level2QuestionsVisibleSchemaGroupNameList.includes(
              schemaGroupName
            )
          ) {
            notVisible = false;
          } else if (
            !!level3QuestionsVisibleSchemaGroupNameList.includes(
              schemaGroupName
            ) &&
            visibilityFromIntersection
          ) {
            notVisible = false;
          }

          if (typeof value === "undefined" || notVisible) {
            acc[key] = "";
          } else if (Array.isArray(value)) {
            acc[key] = value;
          } else {
            acc[key] = String(value);
          }
          return acc;
        }, {});

        const params = {
          variables: {
            id: Number(questionId),
            values: JSON.stringify(formattedAnswer),
          },
        };
        formQuestionAnswer(params);
      } catch (e) {
        console.error("エラーが発生しました:", e);
      }
    },
    [
      formQuestionAnswer,
      questionId,
      allQuestionWithHierarchy,
      level2QuestionsVisibleSchemaGroupNameList,
      level3QuestionsVisibleSchemaGroupNameList,
      selectedLevel1Values,
    ]
  );

  return (
    <form
      noValidate
      onSubmit={handleSubmit(handleAnswerSubmit)}
      className={processStyles.questionContainer}
    >
      <h2>アンケート</h2>
      <div className={processStyles.contentQuestionWrapper}>
        <div className={processStyles.questionnaireContent}>
          <p
            className={`${processStyles.questionnaireDescription} ${processStyles.level1Description}`}
          >
            よろしければ、今後のサービス改善のためアンケートにご協力ください
          </p>
          <div
            className={`${styles.questionSurvey} ${styles.questionSurveyTS}`}
          >
            <div className={processStyles.questionnaireDescription} />
            {level1Questions?.map((question, index) => {
              if (question === null) return null;
              const subTitle = getSubTitle(1, index + 1);
              if (question.type === "string") {
                return (
                  <TextQuestion
                    key={`question-${index}`}
                    register={register}
                    subTitle={subTitle}
                    question={question}
                    errors={errors}
                    title={question.label}
                    visible={true}
                  />
                );
              } else if (question.type === "const") {
                return (
                  <CheckboxQuestion
                    key={`question-${index}`}
                    control={control as any}
                    question={question}
                    subTitle={subTitle}
                    errors={errors}
                    selectedQuestionValues={(values) =>
                      handleLevel1OptionChange(question.key_name || "", values)
                    }
                    visible={true}
                  />
                );
              }
            })}
            {level2Questions?.map((question, index) => {
              if (question === null) return null;
              const subTitle = getSubTitle(2, index + 1);
              if (question.type === "string") {
                return (
                  <TextQuestion
                    key={`question-${index}`}
                    register={register}
                    subTitle={subTitle}
                    question={question}
                    errors={errors}
                    title={question.label}
                    visible={
                      !!level2QuestionsVisibleSchemaGroupNameList.includes(
                        question?.schema_group_name
                      )
                    }
                  />
                );
              } else if (question.type === "const") {
                return (
                  <CheckboxQuestion
                    key={`question-${index}`}
                    control={control as any}
                    question={question}
                    subTitle={subTitle}
                    errors={errors}
                    selectedQuestionValues={(values) =>
                      handleLevel2OptionChange(question.key_name || "", values)
                    }
                    visible={
                      !!level2QuestionsVisibleSchemaGroupNameList.includes(
                        question?.schema_group_name
                      )
                    }
                  />
                );
              }
              return null;
            })}
            {level3QuestionsNotNull?.map((question, index) => {
              if (question === null) return null;
              const subTitle = getSubTitle(3, index + 1);

              const visibleFromLevel1Selected = Object.values(
                selectedLevel1Values
              )
                .flat()
                .flat();

              const hierarchyList = question?.hierarchy;

              const visibilityFromIntersection =
                visibleFromLevel1Selected?.filter((element) =>
                  hierarchyList.includes(element)
                )?.length > 0;
              if (question?.type === "string") {
                return (
                  <TextQuestion
                    key={`question-${index}`}
                    register={register}
                    question={question}
                    subTitle={subTitle}
                    errors={errors}
                    title={question?.label}
                    visible={
                      !!level3QuestionsVisibleSchemaGroupNameList.includes(
                        question?.schema_group_name
                      ) && visibilityFromIntersection
                    }
                  />
                );
              } else if (question?.type === "const") {
                return (
                  <CheckboxQuestion
                    key={`question-${index}`}
                    control={control as any}
                    question={question}
                    subTitle={subTitle}
                    errors={errors}
                    selectedQuestionValues={() => {}}
                    visible={
                      !!level3QuestionsVisibleSchemaGroupNameList.includes(
                        question?.schema_group_name
                      ) && visibilityFromIntersection
                    }
                  />
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
      <div
        className={`${formStyles.btnArea} ${formStyles.multiple} ${formStyles.minWidth10em}`}
      >
        {isSkipAble && (
          <button
            type="button"
            onClick={() => hideModal()}
            className={styleButton.backButtonStyled}
          >
            戻る
          </button>
        )}
        <TSButton
          fillCautionColor
          fontFixed
          type="submit"
          disabled={!isValid}
          loading={isSubmitting}
        >
          回答
        </TSButton>
      </div>
    </form>
  );
}
